'use client';

import { ElementType, useContext, useEffect, useMemo } from 'react';

import { Container, IconName, Section, Spacer, SpacerSize, ToasterContext, TrilogyColor } from '@bytel/trilogy-react';

import { ConfigType } from '@app-types/api';
import { ContentEditorItem } from '@app-types/cms';
import { FaiProduct } from '@app-types/fai';
import { JOURNEYS, OptionType, PlanType, RepeaterProduct } from '@app-types/product';

import CatalogOffers from '@components/home/catalog-offers';
import { HomeBanner } from '@components/home/home-banner';
import { SSIBlock } from '@components/ssi-block';
import { TriggerLogin } from '@components/trigger-login';

import useAbTest from '@hooks/use-abtest';
import { useScrollBelow } from '@hooks/use-below';
import { useGetOffersConnected } from '@hooks/use-get-offers-connected';

import { PHONE_AB_TEST } from '@helpers/abtest-utils';
import { getHomeBannerData } from '@helpers/home-banner';
import { isPack } from '@helpers/product';

import { HomeTabs } from '@landing/components';
import { cartStore } from '@stores/cart';
import { ToasterAlertPosition } from '@trilogy-ds/react/lib/components/alert/AlertProps';

import PageBottomContent from './home/bottom-content/page-bottom-content';
import HomePageConfigurators from './home/home-page-configurators';
import StickySummary from './sticky-footer/sticky-summary';

export function Configurator({
    products: _products,
    faiProducts: _faiProducts,
    blockedOption,
    config,
    repeaters,
    rocs,
    faiOrder,
    journey,
    catalogOffers,
}: Readonly<{
    products: PlanType[];
    faiProducts: FaiProduct[];
    blockedOption?: OptionType;
    config?: ConfigType;
    repeaters?: { [_: string]: RepeaterProduct };
    rocs: ContentEditorItem[];
    faiOrder: string[];
    journey: JOURNEYS;
    catalogOffers: PlanType[];
}>) {
    const otisConfigurator = cartStore((state) => state.otisConfigurator);
    const setOtisConfigurator = cartStore((state) => state.setOtisConfigurator);
    const { isBelow, ref: sectionRef } = useScrollBelow();
    const { show } = useContext(ToasterContext);
    const { products, faiProducts, isLoading } = useGetOffersConnected(_products, _faiProducts, rocs, faiOrder);

    const currentJourney = otisConfigurator.value ?? journey;
    const isMobilePlanFirst = currentJourney !== JOURNEYS.FAI || faiProducts.length === 0;
    const isPackPage = currentJourney === JOURNEYS.PACKS;

    const { abTestVariant: abTestPhoneVariant } = useAbTest(
        PHONE_AB_TEST,
        config?.ab_test_boost_taux_ouverture,
        'step_phone',
        false,
    );

    const bannerUrl = useMemo(() => {
        switch (currentJourney) {
            case JOURNEYS.BYOU:
                return '/partials/bandeau/forfaits-mobiles_sans-engagement';
            case JOURNEYS.FAI:
                return '/partials/bandeau/offres-internet_sans-engagement';
            case JOURNEYS.PACKS:
                return '/partials/bandeau/forfaits-packs_sans-engagement';
        }
    }, [currentJourney]);

    const hasPacks = useMemo(() => products.some(isPack), [products]);

    useEffect(() => {
        setOtisConfigurator(journey);
        if (config?.toaster_title && faiProducts.length > 0) {
            show({
                title: config?.toaster_title,
                description: config.toaster_description,
                duration: config.toaster_duration_ms ?? 6000,
                id: 'bundle-toaster',
                // @ts-expect-error - work with "other" props
                className: 'w-fit toaster-top-right',
                position: ToasterAlertPosition.TOP,
                iconName: (config.toaster_icon as IconName) || IconName.INFOS_CIRCLE,
            });
        }
    }, []);

    return (
        <>
            <TriggerLogin />
            <SSIBlock key={Number(isMobilePlanFirst)} url={bannerUrl} />
            <HomeBanner banner={getHomeBannerData({ config, isPack: isPackPage, isMobilePlanFirst })} />
            <Section ref={sectionRef} backgroundColor={TrilogyColor.NEUTRAL_FADE}>
                <HomeTabs journey={currentJourney} hasPacks={hasPacks} abTestPhoneVariant={abTestPhoneVariant} />
                <Spacer size={SpacerSize.FOUR} />
                <Container medium>
                    <HomePageConfigurators
                        journey={currentJourney}
                        plans={products}
                        repeaters={repeaters}
                        faiProducts={faiProducts}
                        isMobileFirst={isMobilePlanFirst}
                        isLoading={isLoading}
                        config={config}
                    />
                </Container>
            </Section>
            <StickySummary
                mobileFirst={isMobilePlanFirst}
                isStickyVisible={isBelow}
                repeaters={repeaters}
                config={config}
                isLoading={isLoading}
            />
            <CatalogOffers catalog={catalogOffers} blockedOption={blockedOption} />
            <SSIBlock<ElementType>
                url={isMobilePlanFirst ? '/partials/byou/presentation-mobile' : '/partials/byou/presentation-fai'}
            />
            <PageBottomContent mobilePlanFirst={isMobilePlanFirst} />
        </>
    );
}
